import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Zap, Check, ArrowRight, Key, Play, TrendingDown, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import confetti from "canvas-confetti";
import VoiceHint from "@/components/VoiceHint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { label: "Analyzing sample workflow...", duration: 2000 },
  { label: "Finding optimal routes across 40+ providers...", duration: 2000 },
  { label: "Calculating your savings...", duration: 1000 },
];

export default function FirstOptimizationCard({ onOptimizationComplete }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle");
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [managedStatus, setManagedStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("qn_hero_dismissed") === "true");

  useEffect(() => {
    axios.get(`${API}/managed-keys/status`).then(r => setManagedStatus(r.data)).catch(() => {});
  }, []);

  const runOptimization = useCallback(async () => {
    setStatus("running");
    setCurrentStep(0);
    setProgress(0);

    const totalDuration = STEPS.reduce((s, step) => s + step.duration, 0);
    let elapsed = 0;

    for (let i = 0; i < STEPS.length; i++) {
      setCurrentStep(i);
      const stepDur = STEPS[i].duration;
      const startProgress = (elapsed / totalDuration) * 100;
      const endProgress = ((elapsed + stepDur) / totalDuration) * 100;

      const interval = setInterval(() => {
        setProgress(prev => Math.min(prev + 2, endProgress));
      }, stepDur / ((endProgress - startProgress) / 2));

      await new Promise(resolve => setTimeout(resolve, stepDur));
      clearInterval(interval);
      setProgress(endProgress);
      elapsed += stepDur;
    }

    try {
      const response = await axios.post(`${API}/managed-keys/optimize`);
      setResult(response.data);
      setStatus("complete");
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      if (onOptimizationComplete) onOptimizationComplete(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail || "Optimization failed. Please try again.";
      toast.error(msg);
      setStatus("idle");
      setProgress(0);
    }
  }, [onOptimizationComplete]);

  const runAnother = useCallback(async () => {
    setResult(null);
    setStatus("idle");
    setProgress(0);
    setTimeout(() => runOptimization(), 100);
  }, [runOptimization]);

  if (dismissed && !result) return null;

  if (status === "complete" && result) {
    const saved = result.original_cost - result.optimized_cost;
    const dailySavings = (saved * 1000).toFixed(0);
    const remaining = result.remaining_managed;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        data-testid="first-optimization-results"
      >
        <Card className="bg-[var(--qn-card)] border-[var(--qn-success)]/30 mb-8 overflow-hidden">
          <CardContent className="p-8">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 rounded-full bg-[var(--qn-success)]/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-[var(--qn-success)]" />
              </div>
              <h2 className="text-xl font-semibold font-outfit">Your First Optimization Complete!</h2>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-[var(--qn-background)] rounded-xl p-5 text-center">
                <div className="text-sm text-[var(--qn-foreground-subtle)] mb-1">Before</div>
                <div className="text-2xl font-bold font-mono">${result.original_cost.toFixed(4)}</div>
                <div className="text-xs text-[var(--qn-foreground-subtle)] mt-1">({result.original_model})</div>
              </div>
              <div className="bg-[var(--qn-background)] rounded-xl p-5 text-center">
                <div className="text-sm text-[var(--qn-foreground-subtle)] mb-1">After</div>
                <div className="text-2xl font-bold font-mono text-[var(--qn-primary)]">${result.optimized_cost.toFixed(4)}</div>
                <div className="text-xs text-[var(--qn-foreground-subtle)] mt-1">({result.optimized_model})</div>
              </div>
              <div className="bg-[var(--qn-success)]/10 rounded-xl p-5 text-center border border-[var(--qn-success)]/20">
                <div className="text-sm text-[var(--qn-foreground-subtle)] mb-1">Saved</div>
                <div className="text-2xl font-bold font-mono text-[var(--qn-success)]">{result.cost_reduction_percent}%</div>
                <div className="text-xs text-[var(--qn-success)] mt-1">${saved.toFixed(4)}</div>
              </div>
            </div>

            <p className="text-[var(--qn-foreground-muted)] mb-6">
              Imagine this on every API call you make.
              At 1,000 calls/day, that's <span className="text-[var(--qn-success)] font-semibold">${dailySavings}/month saved</span>.
            </p>

            <div className="flex gap-3">
              <Button
                onClick={() => navigate("/settings")}
                className="btn-primary"
                data-testid="connect-keys-btn"
                data-automation-id="connect-keys-btn"
              >
                <Key className="w-4 h-4 mr-2" />
                Connect My AI Keys
                <span className="ml-2 text-xs opacity-70">(unlock unlimited)</span>
              </Button>
              {remaining > 0 && (
                <Button
                  onClick={runAnother}
                  variant="outline"
                  className="border-[var(--qn-primary)]/30 hover:bg-[var(--qn-primary-muted)]"
                  data-testid="run-another-btn"
                  data-automation-id="run-another-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Run Another ({remaining} left)
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  if (status === "running") {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        data-testid="first-optimization-running"
      >
        <Card className="bg-[var(--qn-card)] border-[var(--qn-primary)]/30 mb-8">
          <CardContent className="p-8">
            <div className="space-y-6">
              <Progress value={progress} className="h-2" />
              <div className="space-y-3">
                {STEPS.map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    {i < currentStep ? (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-5 h-5 rounded-full bg-[var(--qn-success)] flex items-center justify-center shrink-0"
                      >
                        <Check className="w-3 h-3 text-white" />
                      </motion.div>
                    ) : i === currentStep ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 rounded-full border-2 border-[var(--qn-primary)]/30 border-t-[var(--qn-primary)] shrink-0"
                      />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-[var(--qn-border)] shrink-0" />
                    )}
                    <span className={`text-sm ${i <= currentStep ? "text-[var(--qn-foreground)]" : "text-[var(--qn-foreground-subtle)]"}`}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  const remaining = managedStatus?.remaining ?? 10;
  const hasOptimized = managedStatus?.has_optimized ?? false;

  if (hasOptimized && managedStatus?.has_own_keys) return null;

  if (hasOptimized && !managedStatus?.has_own_keys) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="managed-key-banner"
      >
        <div className="mb-6 px-5 py-3 rounded-xl bg-[var(--qn-primary-muted)] border border-[var(--qn-primary)]/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-[var(--qn-foreground)]">
              You have <strong className="text-[var(--qn-primary)]">{remaining} free optimizations</strong> left this month. Connect your API keys for unlimited savings.
            </span>
            <VoiceHint context="connect-keys" label="Get help connecting API keys" />
          </div>
          <Button
            onClick={() => navigate("/settings")}
            size="sm"
            variant="outline"
            className="border-[var(--qn-primary)]/30 text-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/10 shrink-0 ml-4"
            data-testid="banner-connect-keys-btn"
            data-automation-id="banner-connect-keys-btn"
          >
            <Key className="w-3 h-3 mr-1.5" />
            Connect Keys
            <ArrowRight className="w-3 h-3 ml-1.5" />
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="first-optimization-hero"
    >
      <Card className="bg-gradient-to-br from-[var(--qn-card)] to-[var(--qn-primary)]/5 border-[var(--qn-primary)]/20 mb-8 overflow-hidden">
        <CardContent className="p-8 md:p-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-6 h-6 text-[var(--qn-primary)]" />
              <h2 className="text-2xl font-bold font-outfit">See How Much You Can Save</h2>
            </div>
            <p className="text-[var(--qn-foreground-muted)] mb-6 text-base leading-relaxed">
              QNNEAL analyzes your AI workflows and finds more cost-effective, faster routes — automatically. No setup required.
            </p>

            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                onClick={runOptimization}
                className="btn-primary py-6 px-8 text-lg rounded-xl shadow-lg shadow-[var(--qn-primary)]/20"
                data-testid="run-first-optimization-btn"
                data-automation-id="run-first-optimization-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Run My First Optimization
              </Button>
            </motion.div>

            <div className="flex items-center gap-6 mt-5 text-sm text-[var(--qn-foreground-subtle)]">
              <span className="flex items-center gap-1.5">
                <Check className="w-4 h-4 text-[var(--qn-success)]" /> No API key needed
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="w-4 h-4 text-[var(--qn-success)]" /> Takes 10 seconds
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="w-4 h-4 text-[var(--qn-success)]" /> 100% free
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
